// @flow
import React, { Component } from "react"
import styled from "styled-components"
import _ from "lodash"
import * as strings from "../strings"

import FontIcon from "react-toolbox/lib/font_icon"
import Checkbox from "react-toolbox/lib/checkbox"
import ArrowIcon from "./ArrowIcon"
import Spacer from "./Spacer"


type Value = {};
type Column = {
  title: string,
  align: "center" | "left" | "right",
  width: string,
};
type Columns = { [string]: Column };
// type Action = {
//   label: string,
//   icon: string,
//   onClick: Function,
// };

const Table = styled.table`
  width: 100%;
  table-layout: auto;
`

const HeaderContainer = styled.th`
  color: #9e9e9e;
  padding-bottom: 10px;
  padding-top: 10px;
  text-align: ${ props => props.align };
`

const justifyAligns = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
}

const CheckboxWrapper = styled.div`
  &> * {
    margin-bottom: 0px !important;
  }
`

const HeaderContainerInner = styled.div`
  display: flex;
  width: 100%;
  justify-content: ${ props => justifyAligns[ props.align ] };
`

const HeaderTitle = styled.div`
  line-height: 22px;
  cursor: pointer;
`

const ActionWrapper = styled.div`
  cursor: ${ props => props.onClick ? "pointer" : "initial" };
  user-select: none;
`

const Header = ( {
  title,
  direction,
  align,
  onClick,
}: Column & { direction: ?( "asc" | "desc" ) } ) => (
  <HeaderContainer align={align} onClick={onClick}>
    <HeaderContainerInner align={align}>
      <HeaderTitle>{title}</HeaderTitle>
      <ArrowIcon
        invert={direction !== "desc"}
        iconStyle={{
          color: (direction !== null) ? "#2196f3" : "#E0E0E0",
        }}
      />
    </HeaderContainerInner>
  </HeaderContainer>
)

const HeaderSelect = ( {
  checked,
  onChange
} ) => (
  <HeaderContainer  style={{ width: "120px" }}>
    <HeaderContainerInner>
      <CheckboxWrapper>
        <Checkbox
          style={{ marginBottom: "0px" }}
          checked={checked}
          onChange={onChange}
        />
      </CheckboxWrapper>
    </HeaderContainerInner>
  </HeaderContainer>
)


const Row = styled.tr`
  background-color: ${ ( { striped } ) => ( striped ? "rgba(0, 0, 0, 0.08)" : "white" ) };
  height: 30px;
  &>td:first-child, >th:first-child{
    padding-left: ${ ( { leftPadding } ) => ( leftPadding || "80px" ) };
  }
  &>td:last-child, >th:last-child{
    padding-right: 80px;
  }
`

const Cell = styled.td`
  text-align: ${ props => props.align };
`

const Action = ( { label, icon, onClick } ) => (
  <ActionWrapper onClick={onClick}>
    <FontIcon value={icon} />
  </ActionWrapper>
)

const Item = ( {
  value,
  columns,
  striped,
  onClick,
  style,
  leftPadding,
  actions,
  selectable,
  selected,
  toggleSelect,
}: {
  value: Value,
  columns: Columns,
  striped: boolean,
  onClick: any,
  style: any,
  leftPadding: any,
  actions: any,
  selectable: boolean,
  selected: boolean,
  toggleSelect: any,
} ) => (
  <Row striped={striped} leftPadding={leftPadding}>
    {selectable ? (
      <Cell key="selectable" align="left">
        <CheckboxWrapper>
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
          />
        </CheckboxWrapper>
      </Cell>
    ) : null}
    {_.map( columns, ( column, key ) => (
      <Cell key={key} onClick={onClick} style={{
        ...style,
        width: column.width,
      }} align={column.align}>{value[ key ]}</Cell>
    ) )}
    {actions ? (
      <Cell key="actions" align="right">
        {actions.map( ( action, key ) => (
          <Action {...action} key={key} />
        ) )}
      </Cell>
    ) : null}
  </Row>
)

export default class SimpleTable extends Component {
  props: {
    columns: Columns,
    data: Value[],
    dataFormatter: Function,
    leftPadding: boolean,
    sortable?: boolean,

    selectable?: boolean,
    selectKey?: string,
    selectedItems?: [],
    onSelectedItemsChange?: any,
    style: any,
  };

  state = {
    sort: null,
  };

  changeSelectedItems = newSelectedItems => {
    if ( this.props.onSelectedItemsChange ) {
      this.props.onSelectedItemsChange( newSelectedItems )
    }
  }

  allSelected = () => {
    const { data, selectedItems } = this.props

    return data.length === selectedItems.length
  }

  getSelectKeyValue = item => {
    const { selectKey } = this.props

    if (selectKey) {
      return _.get( item, selectKey )
    } else {
      return item
    }
  }

  toggleAllSelected =() => {
    const { data } = this.props
    const allSelected = this.allSelected()

    const newSelectedItems = allSelected
      ? []
      : data.map( item => this.getSelectKeyValue(item) )

    this.changeSelectedItems( newSelectedItems )
  }

  toggleSelectItem = item => () => {
    const { selectedItems } = this.props

    const itemKey = this.getSelectKeyValue(item)
    const isSelected = this.isItemSelected( item )

    const newSelectedItems = isSelected
      ? selectedItems.filter( si => si !== itemKey )
      : [ ...selectedItems, itemKey ]

    this.changeSelectedItems( newSelectedItems )
  }

  isItemSelected = item => {
    const { selectedItems, selectable } = this.props

    if ( !selectable ) {
      return false
    }

    const itemKey = this.getSelectKeyValue(item)
    return !!selectedItems.find(si => _.isEqual(si, itemKey))
  }

  render () {
    let { columns, data, leftPadding, sortable, selectable, dataFormatter, style } = this.props
    sortable = ( sortable === undefined ) ? true : sortable
    const { sort } = this.state
    if ( sort && sortable ) {
      data = _.sortBy( data, sort.key )
      if ( sort.direction === "desc" ) {
        data = data.reverse()
      }
    }
    if (dataFormatter) {
      data = data.map(dataFormatter);
    }
    const hasActions = !!data.find( d => d._actions )

    return (
      <Table cellPadding="10" cellSpacing="0">
        <thead>
          <Row leftPadding={leftPadding}>
            {selectable ? <HeaderSelect
              checked={this.allSelected()}
              onChange={this.toggleAllSelected}
            /> : null}
            {_.map( columns, ( column, key ) => (
              <Header
                {...column}
                direction={sort && sort.key === key ? sort.direction : null}
                key={key}
                onClick={() => {
                  if ( sortable ) {
                    this.setState( {
                      sort: {
                        key,
                        direction: sort && sort.direction === "desc" ? "asc" : "desc",
                      },
                    } )
                  }
                } }
              />
            ) )}
            {hasActions ? <Header
              title={strings.generalStrings.actions}
              align="right"
              direction={null}
              key="actions"
            /> : null}
          </Row>
        </thead>
        <tbody>
          {data.map( ( value, index ) => {
            return (
              <Item
                leftPadding={leftPadding}
                value={value}
                columns={columns}
                striped={index % 2}
                key={index}
                actions={value._actions}
                onClick={value._onClick}
                selectable={selectable}
                selected={this.isItemSelected(value)}
                toggleSelect={this.toggleSelectItem(value)}
                style={{
                  cursor: value._onClick ? "pointer" : "initial",
                  color: value.status === "Ativo" ? "#99cc00" : "#777777",
                  ..._.get(style, "item"),
                }}
              />
            ) } )}
        </tbody>
      </Table>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/SimpleTable.jsx